import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import type { User } from '../../services/users/users.service';
import { ConfirmationToast } from '../ui/ConfirmationToast';
import { NotificationToast } from '../ui/NotificationToast';
import { usePermissions } from '../../hooks/usePermissions';

interface UserDetailsDrawerProps {
  isOpen: boolean;
  user: User | null;
  onClose: () => void;
  onEditUser: (user: User) => void;
  onToggleUserStatus: (user: User) => Promise<void>;
}

export const UserDetailsDrawer: React.FC<UserDetailsDrawerProps> = ({
  isOpen,
  user,
  onClose,
  onEditUser,
  onToggleUserStatus
}) => {
  const { hasPermission } = usePermissions();
  const [isVisible, setIsVisible] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [activeTab, setActiveTab] = useState<'info' | 'roles'>('info');
  
  // Verificar permisos
  const canEditUsers = hasPermission('Administradores', 'editar');
  const canDeleteUsers = hasPermission('Administradores', 'eliminar');
  const canAdministerUsers = hasPermission('Administradores', 'administrar');
  const canActivateUsers = hasPermission('Administradores', 'activar');
  const canDeactivateUsers = hasPermission('Administradores', 'desactivar');
  
  // Estados para confirmación y loading
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState<'toggle' | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Estados para notificaciones
  const [showNotification, setShowNotification] = useState(false);
  const [notificationConfig, setNotificationConfig] = useState<{
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);

  useEffect(() => {
    if (isOpen && user) {
      setCurrentUser(user);
      // Pequeño delay para asegurar que el componente se monte antes de la animación
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 10);
      return () => clearTimeout(timer);
    } else if (!isOpen) {
      setIsVisible(false);
    }
  }, [isOpen, user]);

  // Limpiar el currentUser después de la animación de salida
  useEffect(() => {
    if (!isVisible && !isOpen) {
      const timer = setTimeout(() => {
        setCurrentUser(null);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isVisible, isOpen]);

  const handleClose = () => {
    setIsVisible(false);
    // Llamar onClose inmediatamente - el componente padre maneja el estado
    onClose();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatLastAccess = (dateString?: string) => {
    if (!dateString) return 'Nunca';
    return formatDate(dateString);
  };

  const getTipoUsuarioColor = (tipo: string) => {
    switch (tipo) {
      case 'administrador':
        return 'bg-blue-100 text-blue-800';
      case 'cliente':
        return 'bg-green-100 text-green-800';
      case 'vendedor':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getEstadoColor = (estado: boolean) => {
    return estado 
      ? 'bg-green-100 text-green-800' 
      : 'bg-red-100 text-red-800';
  };

  // Funciones para manejar las acciones
  const handleEditUser = () => {
    if (currentUser) {
      onEditUser(currentUser);
    }
  };

  const handleToggleUserStatus = () => {
    setConfirmationAction('toggle');
    setShowConfirmation(true);
  };


  const handleConfirmAction = async () => {
    if (!currentUser || !confirmationAction) return;
    
    setIsLoading(true);
    try {
      if (confirmationAction === 'toggle') {
        await onToggleUserStatus(currentUser);
        
        // Mostrar toast de éxito
        setNotificationConfig({
          title: '¡Éxito!',
          message: `Usuario ${currentUser.estado ? 'desactivado' : 'activado'} correctamente`,
          type: 'success'
        });
        setShowNotification(true);
      }
    } catch (error) {
      console.error('Error al ejecutar acción:', error);
      
      // Mostrar toast de error
      setNotificationConfig({
        title: 'Error',
        message: 'No se pudo cambiar el estado del usuario. Inténtalo nuevamente.',
        type: 'error'
      });
      setShowNotification(true);
    } finally {
      setIsLoading(false);
      setShowConfirmation(false);
      setConfirmationAction(null);
    }
  };

  const handleCancelAction = () => {
    setShowConfirmation(false);
    setConfirmationAction(null);
  };

  const getConfirmationConfig = () => {
    if (!currentUser || !confirmationAction) return null;
    
    if (confirmationAction === 'toggle') {
      return {
        title: currentUser.estado ? 'Desactivar Usuario' : 'Activar Usuario',
        message: currentUser.estado 
          ? `¿Estás seguro de que deseas desactivar a ${currentUser.nombreCompleto}? El usuario no podrá acceder al sistema.`
          : `¿Estás seguro de que deseas activar a ${currentUser.nombreCompleto}? El usuario podrá acceder al sistema nuevamente.`,
        confirmText: currentUser.estado ? 'Desactivar' : 'Activar',
        type: currentUser.estado ? 'danger' as const : 'success' as const
      };
    }
    
    return null;
  };

  if (!isOpen && !currentUser) return null;
  if (!currentUser) return null;

  return (
    <div className="fixed inset-0 z-20 overflow-hidden" style={{ top: '90px' }}>
      <div className="absolute inset-0 overflow-hidden">
        {/* Overlay invisible para cerrar al hacer clic fuera */}
        <div 
          className="absolute inset-0 cursor-pointer"
          onClick={handleClose}
          aria-label="Cerrar drawer"
        />
        
        {/* Drawer */}
        <section className={`absolute right-0 top-0 h-full w-full max-w-4xl border-l border-gray-200 transform transition-transform duration-300 ease-in-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}>
          <div 
            className="h-full flex flex-col bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-4 bg-gray-100 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  Detalles del Usuario
                </h2>
                <button
                  onClick={handleClose}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {/* Avatar y nombre - Siempre visible */}
              <div className="text-center py-6 bg-gradient-to-b from-gray-50 to-white">
                <div className="mx-auto h-20 w-20 rounded-full bg-gradient-to-r from-purple-400 to-blue-500 flex items-center justify-center mb-3 shadow-lg">
                  <span className="text-white text-2xl font-bold">
                    {currentUser.nombreCompleto.charAt(0).toUpperCase()}
                    {currentUser.nombreCompleto.split(' ')[1]?.charAt(0)?.toUpperCase() || ''}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">
                  {currentUser.nombreCompleto}
                </h3>
                <p className="text-gray-600 mb-2">{currentUser.email}</p>
                <div className="flex justify-center space-x-2">
                  <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${getTipoUsuarioColor(currentUser.tipoUsuario)}`}>
                    {currentUser.tipoUsuario}
                  </span>
                  <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${getEstadoColor(currentUser.estado)}`}>
                    {currentUser.estado ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
              </div>

              {/* Tabs */}
              <div className="px-6 border-b border-gray-200">
                <div className="flex space-x-8">
                  <button
                    onClick={() => setActiveTab('info')}
                    className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'info'
                        ? 'text-purple-600 border-purple-600'
                        : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Información General
                  </button>
                  <button
                    onClick={() => setActiveTab('roles')}
                    className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'roles'
                        ? 'text-purple-600 border-purple-600'
                        : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Roles y Permisos
                  </button>
                </div>
              </div>

              {/* Contenido de las pestañas */}
              <div className="px-6 py-6">
                {activeTab === 'info' && (
                  <div>
                    {/* Información básica */}
                    <div className="mb-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                        Información Básica
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-500">Tipo de Usuario</label>
                          <p className="text-gray-900 font-medium capitalize">{currentUser.tipoUsuario}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-500">Estado</label>
                          <div className="flex items-center space-x-2">
                            <div className={`w-3 h-3 rounded-full ${currentUser.estado ? 'bg-green-500' : 'bg-red-500'}`}></div>
                            <p className="text-gray-900 font-medium">{currentUser.estado ? 'Activo' : 'Inactivo'}</p>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-500">Requiere Cambio de Contraseña</label>
                          <div className="flex items-center space-x-2">
                            <div className={`w-3 h-3 rounded-full ${currentUser.requiereCambioContrasena ? 'bg-orange-500' : 'bg-green-500'}`}></div>
                            <p className="text-gray-900 font-medium">{currentUser.requiereCambioContrasena ? 'Sí' : 'No'}</p>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-500">Último Acceso</label>
                          <p className="text-gray-900 font-medium">{formatLastAccess(currentUser.ultimoAcceso)}</p>
                        </div>
                      </div>
                    </div>

                    {/* Información específica de membresía */}
                    {currentUser.membresia && (
                      <div className="mb-6">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                          Información de Membresía
                        </h4>
                        <div className="bg-green-50 rounded-lg p-4 space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-500">Nombre Completo</label>
                            <p className="text-gray-900 font-medium">{currentUser.membresia.nombres} {currentUser.membresia.apellidos}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-500">Teléfono</label>
                            <p className="text-gray-900 font-medium">{currentUser.membresia.telefono}</p>
                          </div>
                          {currentUser.membresia.numeroDocumento && (
                            <div>
                              <label className="block text-sm font-medium text-gray-500">Número de Documento</label>
                              <p className="text-gray-900 font-medium">{currentUser.membresia.numeroDocumento}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Información del Sistema */}
                    <div className="pt-4 border-t border-gray-200">
                      <h4 className="text-sm font-medium text-gray-600 mb-3">
                        Información del Sistema
                      </h4>
                      <div className="space-y-2 text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
                        <div><span className="font-medium">ID:</span> {currentUser.id}</div>
                        <div><span className="font-medium">Creado:</span> {formatDate(currentUser.creadoEn)}</div>
                        <div><span className="font-medium">Última actualización:</span> {formatDate(currentUser.actualizadoEn)}</div>
                        {currentUser.membresiaId && (
                          <div><span className="font-medium">ID Membresía:</span> {currentUser.membresiaId}</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'roles' && (
                  <div>
                    {/* Roles y permisos */}
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-semibold text-gray-900">
                          Roles y Permisos Asignados
                        </h4>
                        <span className="text-sm text-gray-500">
                          {currentUser.roles?.length || 0} rol(es) asignado(s)
                        </span>
                      </div>
                      
                      {currentUser.roles && currentUser.roles.length > 0 ? (
                        <div className="space-y-6">
                          {currentUser.roles.map((role) => (
                            <div key={role.id} className="border border-gray-200 rounded-xl p-5 bg-white shadow-sm hover:shadow-md transition-shadow">
                              <div className="flex items-center justify-between mb-4">
                                <div>
                                  <h5 className="font-semibold text-gray-900 text-lg flex items-center">
                                    <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                    </svg>
                                    {role.nombre}
                                  </h5>
                                  {role.descripcion && (
                                    <p className="text-sm text-gray-600 mt-1 ml-7">{role.descripcion}</p>
                                  )}
                                </div>
                                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                                  role.activo 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {role.activo ? 'Activo' : 'Inactivo'}
                                </span>
                              </div>
                              
                              {/* Módulos y permisos */}
                              {role.modulos && role.modulos.length > 0 && (
                                <div>
                                  <h6 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                                    <svg className="w-4 h-4 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                    </svg>
                                    Módulos Autorizados ({role.modulos.length})
                                  </h6>
                                  <div className="grid gap-3">
                                    {role.modulos.map((modulo) => (
                                      <div key={modulo.id} className="bg-gray-50 p-4 rounded-lg border-l-4 border-blue-400">
                                        <div className="flex items-center justify-between mb-2">
                                          <span className="text-sm font-medium text-gray-900 flex items-center">
                                            <svg className="w-4 h-4 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                            </svg>
                                            {modulo.nombre}
                                          </span>
                                          {modulo.ruta && (
                                            <span className="text-xs text-gray-500 font-mono bg-gray-200 px-2 py-1 rounded">
                                              {modulo.ruta}
                                            </span>
                                          )}
                                        </div>
                                        {modulo.descripcion && (
                                          <p className="text-xs text-gray-600 mb-3 ml-6">{modulo.descripcion}</p>
                                        )}
                                        {modulo.permisos && modulo.permisos.length > 0 && (
                                          <div className="ml-6">
                                            <span className="text-xs font-medium text-gray-500 mb-2 block">
                                              Permisos ({modulo.permisos.length}):
                                            </span>
                                            <div className="flex flex-wrap gap-1">
                                              {modulo.permisos.map((permiso) => (
                                                <span 
                                                  key={permiso.id} 
                                                  className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded-md font-medium border border-purple-200 flex items-center"
                                                >
                                                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                  </svg>
                                                  {permiso.nombre}
                                                </span>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12 text-gray-500">
                          <svg className="mx-auto h-16 w-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          <p className="text-lg font-medium mb-2">Sin roles asignados</p>
                          <p className="text-sm text-gray-400">Este usuario no tiene ningún rol configurado en el sistema.</p>
                          <p className="text-sm text-gray-400">Contacte al administrador para asignar los permisos necesarios.</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer con acciones */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <div className="flex space-x-3">
                {canEditUsers && (
                  <button 
                    onClick={handleEditUser}
                    className="flex-1 bg-purple-600 text-white px-4 py-3 rounded-lg hover:bg-purple-700 transition-colors font-medium text-sm"
                  >
                    Editar Usuario
                  </button>
                )}
                {((currentUser.estado && canDeactivateUsers) || (!currentUser.estado && canActivateUsers) || canAdministerUsers) && (
                  <button 
                    onClick={handleToggleUserStatus}
                    disabled={isLoading}
                    className={`flex-1 px-4 py-3 rounded-lg transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed ${
                      currentUser.estado 
                        ? 'bg-red-600 text-white hover:bg-red-700' 
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                  >
                    {currentUser.estado ? 'Desactivar' : 'Activar'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
      
      {/* Confirmation Toast */}
      {/* Confirmation Toast */}
      {(() => {
        const config = getConfirmationConfig();
        return config ? (
          <ConfirmationToast
            isOpen={showConfirmation}
            title={config.title}
            message={config.message}
            confirmText={config.confirmText}
            type={config.type}
            onConfirm={handleConfirmAction}
            onCancel={handleCancelAction}
            isLoading={isLoading}
          />
        ) : null;
      })()}
      
      {/* Notification Toast */}
      {notificationConfig && (
        <NotificationToast
          isOpen={showNotification}
          title={notificationConfig.title}
          message={notificationConfig.message}
          type={notificationConfig.type}
          onClose={() => {
            setShowNotification(false);
            setNotificationConfig(null);
          }}
          duration={7000}
        />
      )}
    </div>
  );
};

export default UserDetailsDrawer;